import { useState } from 'react';
import { Copy, Share2, Users, Gift, Trophy, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useReferrals } from '@/hooks/useReferrals';

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ReferralModal({ isOpen, onClose }: ReferralModalProps) {
  const { 
    referralCode, 
    stats, 
    getShareableLink, 
    copyReferralLink, 
    shareReferralLink,
    loading,
  } = useReferrals();
  
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const success = await copyReferralLink();
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const link = getShareableLink();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="text-accent" size={20} />
            Invite Friends, Earn Rewards
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Hero Section */}
          <div className="text-center space-y-2">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-accent/20 to-primary/20 flex items-center justify-center"
            >
              <Users size={32} className="text-accent" />
            </motion.div>
            <p className="text-muted-foreground text-sm">
              Share your unique link and you'll both receive{' '}
              <span className="font-bold text-accent">100 Aura Points</span>{' '}
              when they sign up!
            </p>
          </div>

          {/* Referral Code */}
          <div className="space-y-3">
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              Your Referral Code
            </label>
            <div className="relative">
              <div className="flex items-center gap-2 p-4 bg-muted/50 rounded-xl border border-border">
                <code className="flex-1 font-mono text-lg font-bold tracking-wider text-center">
                  {loading ? '...' : referralCode}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopy}
                  className="shrink-0"
                >
                  <AnimatePresence mode="wait">
                    {copied ? (
                      <motion.div
                        key="check"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      >
                        <Check size={18} className="text-green-500" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="copy"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      >
                        <Copy size={18} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Button>
              </div>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleCopy}
              variant="outline"
              className="flex-1 gap-2"
            >
              <Copy size={16} />
              Copy Link
            </Button>
            <Button
              onClick={shareReferralLink}
              className="flex-1 gap-2 bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              <Share2 size={16} />
              Share
            </Button>
          </div>

          {/* Stats */}
          {stats.totalReferred > 0 && (
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.totalReferred}</p>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Invited
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-500">
                  {stats.completedReferrals}
                </p>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Joined
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-accent">
                  {stats.totalPointsEarned}
                </p>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Points
                </p>
              </div>
            </div>
          )}

          {/* Rewards Tiers */}
          <div className="space-y-3 pt-4 border-t border-border">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              Referral Rewards
            </p>
            <div className="space-y-2">
              {[
                { count: 1, reward: '100 Aura Points', icon: Gift },
                { count: 5, reward: 'Style Ambassador Badge', icon: Trophy },
                { count: 10, reward: 'Premium Feature Unlock', icon: Trophy },
              ].map((tier, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    stats.completedReferrals >= tier.count
                      ? 'border-green-500/30 bg-green-500/10'
                      : 'border-border bg-muted/30'
                  }`}
                >
                  <tier.icon
                    size={18}
                    className={
                      stats.completedReferrals >= tier.count
                        ? 'text-green-500'
                        : 'text-muted-foreground'
                    }
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{tier.count} Friend{tier.count > 1 ? 's' : ''}</p>
                    <p className="text-xs text-muted-foreground">{tier.reward}</p>
                  </div>
                  {stats.completedReferrals >= tier.count && (
                    <Check size={16} className="text-green-500" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
